import { getAdminNotificationHealth, sendAdminNotificationTest } from '@/lib/api';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getAdminStats, getAdminOrders, updateOrderStatus, adminDeleteOrder, getAdminUsers,
  getAdminDrops, adminCreateDrop, adminUpdateDrop, adminDeleteDrop,
  getAdminCollectibles, adminCreateCollectible, adminUpdateCollectible, adminDeleteCollectible,
  getAdminNews, adminCreateNews, adminDeleteNews,
  getAdminCertificates, updateEngraving, updateOrderShipping,
  getAdminCategories, adminCreateCategory, adminDeleteCategory,
  getAdminSettings, updateAdminSettings, getAdminIbjaRate, updateAdminIbjaRate, getAdminUsersList, adminCreateUser, adminUpdateUser, adminDeleteUser, adminResetUserPassword,
  getAdminPendingCustomRequests, updateCustomRequestStatus
} from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import Navbar from '@/components/Navbar';
import ImageUpload from '@/components/ImageUpload';
import { SalesHubTab } from '@/components/admin/SalesHubTab';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Users, Package, ShoppingBag, TrendingUp, Truck, CheckCircle, Clock, 
  Plus, Edit2, Trash2, X, Calendar, Newspaper, Image, Save, Brain, Settings, UserCog, Key, AlertTriangle, Palette
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'processing', label: 'Processing' },
  { value: 'packed', label: 'Packed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
];

const METAL_TYPES = ['gold', 'silver'];
const GOLD_PURITIES = [
  { label: '24K (999)', fineness: 999, karat: '24K', bis: 'BIS 999' },
  { label: '22K (916)', fineness: 916, karat: '22K', bis: 'BIS 916' },
  { label: '18K (750)', fineness: 750, karat: '18K', bis: 'BIS 750' },
];
const SILVER_PURITIES = [
  { label: '999 Fine', fineness: 999, karat: null, bis: 'BIS 999' },
  { label: '925 Sterling', fineness: 925, karat: null, bis: 'BIS 925' },
];

export default function AdminPage() {
  const [notificationHealth, setNotificationHealth] = useState(null);
  const [notificationTestBusy, setNotificationTestBusy] = useState(false);
  const refreshNotifications = async () => {
    try {
      const [health, latestOrders] = await Promise.all([getAdminNotificationHealth(), getAdminOrders()]);
      setNotificationHealth(health.data); setOrders(latestOrders.data);
    } catch { toast.error('Unable to refresh message status'); }
  };
  const runNotificationTest = async (channel) => {
    setNotificationTestBusy(true);
    try { await sendAdminNotificationTest(channel); toast.success('Owner setup test queued. Delivery is not confirmed yet.'); await refreshNotifications(); }
    catch (error) { toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Unable to queue setup test'); }
    finally { setNotificationTestBusy(false); }
  };

  useEffect(() => { getAdminNotificationHealth().then(r => setNotificationHealth(r.data)).catch(() => setNotificationHealth({unavailable: true})); }, []);

  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [users, setUsers] = useState([]);
  const [drops, setDrops] = useState([]);
  const [collectibles, setCollectibles] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal states
  const [dropModal, setDropModal] = useState({ open: false, mode: 'create', data: null });
  const [collectibleModal, setCollectibleModal] = useState({ open: false, mode: 'create', data: null });
  const [newsModal, setNewsModal] = useState({ open: false });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: '', name: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, oRes, uRes, dRes, cRes, nRes, certRes] = await Promise.all([
          getAdminStats(), getAdminOrders(), getAdminUsers(),
          getAdminDrops(), getAdminCollectibles(), getAdminNews(),
          getAdminCertificates().catch(() => ({ data: [] }))
        ]);
        setStats(sRes.data);
        setOrders(oRes.data);
        setUsers(uRes.data);
        setDrops(dRes.data);
        setCollectibles(cRes.data);
        setNews(nRes.data);
        setCertificates(certRes.data || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    if (user?.is_admin) load();
    else setLoading(false);
  }, [user]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success(`Order updated to ${newStatus}`);
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status: newStatus } : o));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const [shippingModal, setShippingModal] = useState({ open: false, orderId: '', data: {} });

  const openShippingModal = (order) => {
    setShippingModal({
      open: true,
      orderId: order.order_id,
      data: {
        tracking_number: order.tracking_number || '',
        carrier: order.carrier || '',
        estimated_delivery: order.estimated_delivery || '',
        shipping_address: order.shipping_address || '',
      }
    });
  };

  const handleShippingSave = async () => {
    try {
      await updateOrderShipping(shippingModal.orderId, shippingModal.data);
      toast.success('Shipping details updated');
      setOrders(prev => prev.map(o => o.order_id === shippingModal.orderId ? { ...o, ...shippingModal.data } : o));
      setShippingModal({ open: false, orderId: '', data: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async () => {
    const { type, id } = deleteConfirm;
    try {
      if (type === 'drop') {
        await adminDeleteDrop(id);
        setDrops(prev => prev.filter(d => d.drop_id !== id));
      } else if (type === 'collectible') {
        await adminDeleteCollectible(id);
        setCollectibles(prev => prev.filter(c => c.collectible_id !== id));
      } else if (type === 'news') {
        await adminDeleteNews(id);
        setNews(prev => prev.filter(n => n.news_id !== id));
      } else if (type === 'order') {
        await adminDeleteOrder(id);
        setOrders(prev => prev.filter(order => order.order_id !== id));
        setCertificates(prev => prev.filter(cert => cert.order_id !== id));
      }
      toast.success(`${type} deleted successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
    setDeleteConfirm({ open: false, type: '', id: '', name: '' });
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 text-center">
          <LayoutDashboard className="w-16 h-16 text-destructive mx-auto mb-6 opacity-40" />
          <h2 className="font-display text-3xl font-bold mb-4">Admin Access Required</h2>
          <p className="text-muted-foreground">You need admin privileges. The first registered user becomes admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-page">
      <Navbar />
      <div className="grain-overlay" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-6 h-6 text-primary" />
              <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight uppercase">Admin Dashboard</h1>
            </div>
            <Link to="/pothdar">
              <Button variant="outline" className="rounded-full h-9 text-xs gap-2" data-testid="pothdar-link">
                <Brain className="w-4 h-4" /> Pothdar AI
              </Button>
            </Link>
          </div>
          <p className="text-muted-foreground mb-6">Manage drops, collectibles, orders, and content</p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                {[
                  { label: 'Users', value: stats.total_users, icon: Users },
                  { label: 'Orders', value: stats.total_orders, icon: Package },
                  { label: 'Collectibles', value: stats.total_collectibles, icon: ShoppingBag },
                  { label: 'Revenue', value: `Rs ${stats.total_revenue?.toLocaleString()}`, icon: TrendingUp },
                  { label: 'Gold Sold', value: `${stats.total_gold_weight || 0}g`, icon: ShoppingBag, accent: 'text-gold' },
                  { label: 'Silver Sold', value: `${stats.total_silver_weight || 0}g`, icon: ShoppingBag, accent: 'text-silver' },
                ].map((s, i) => (
                  <motion.div key={s.label} className="bg-card border border-border/40 p-4 rounded-sm" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <s.icon className={`w-5 h-5 mb-2 ${s.accent || 'text-primary'}`} />
                    <p className={`font-display text-xl font-bold ${s.accent || ''}`}>{s.value}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">{s.label}</p>
                  </motion.div>
                ))}
              </div>
            )}

            <Separator className="bg-white/10 mb-6" />

            {/* Main Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-secondary border border-border/40 mb-6 flex-wrap h-auto gap-1 p-1">
                <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">Overview</TabsTrigger>
                <TabsTrigger value="saleshub" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-sales-hub-tab"><TrendingUp className="w-3 h-3 mr-1" />Sales Hub</TabsTrigger>
                <TabsTrigger value="drops" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">Drops ({drops.length})</TabsTrigger>
                <TabsTrigger value="collectibles" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-collectibles-tab">Collectibles ({collectibles.length})</TabsTrigger>
                <TabsTrigger value="orders" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-orders-tab">Orders ({orders.length})</TabsTrigger>
                <TabsTrigger value="news" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">News ({news.length})</TabsTrigger>
                <TabsTrigger value="usersmgmt" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs"><UserCog className="w-3 h-3 mr-1" />Users</TabsTrigger>
                <TabsTrigger value="certificates" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs">Certificates ({certificates.length})</TabsTrigger>
                <TabsTrigger value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-settings-tab"><Settings className="w-3 h-3 mr-1" />Settings</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview">
                {stats && (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 rounded-sm text-center">
                      <Clock className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                      <p className="font-display text-xl font-bold">{stats.orders_processing}</p>
                      <p className="text-xs text-muted-foreground">Processing</p>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/20 p-4 rounded-sm text-center">
                      <Truck className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                      <p className="font-display text-xl font-bold">{stats.orders_shipped}</p>
                      <p className="text-xs text-muted-foreground">Shipped</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-sm text-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-1" />
                      <p className="font-display text-xl font-bold">{stats.orders_delivered}</p>
                      <p className="text-xs text-muted-foreground">Delivered</p>
                    </div>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-card border border-border/40 p-4 rounded-sm">
                    <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" /> Recent Drops</h3>
                    {drops.slice(0, 3).map(d => (
                      <div key={d.drop_id} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
                        <img src={d.image_url} alt="" className="w-10 h-10 rounded object-cover bg-muted" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{d.name}</p>
                          <p className="text-xs text-muted-foreground">{d.release_date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-card border border-border/40 p-4 rounded-sm">
                    <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-primary" /> Recent Orders</h3>
                    {orders.slice(0, 3).map(o => (
                      <div key={o.order_id} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{o.collectible_name}</p>
                          <p className="text-xs text-muted-foreground">₹{o.purchase_price?.toLocaleString()}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{o.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Drops Tab */}
              <TabsContent value="saleshub">
                <SalesHubTab collectibles={collectibles} />
              </TabsContent>

              {/* Drops Tab */}
              <TabsContent value="drops">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-display font-bold">Manage Drops</h3>
                  <Button size="sm" onClick={() => setDropModal({ open: true, mode: 'create', data: null })} className="bg-primary text-primary-foreground">
                    <Plus className="w-4 h-4 mr-1" /> New Drop
                  </Button>
                </div>
                <div className="space-y-3">
                  {drops.map(drop => (
                    <div key={drop.drop_id} className="bg-card border border-border/40 rounded-sm p-4 flex items-center gap-4">
                      <img src={drop.image_url} alt="" className="w-16 h-16 rounded object-cover bg-muted flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display font-bold text-sm">{drop.name}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-1">{drop.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{drop.status}</Badge>
                          <span className="text-xs text-muted-foreground">{drop.release_date}</span>
                          <span className="text-xs text-muted-foreground">• {drop.collectible_ids?.length || 0} items</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => setDropModal({ open: true, mode: 'edit', data: drop })}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteConfirm({ open: true, type: 'drop', id: drop.drop_id, name: drop.name })}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {drops.length === 0 && <p className="text-muted-foreground text-center py-8">No drops yet. Create your first drop!</p>}
                </div>
              </TabsContent>

              {/* Collectibles Tab */}
              <TabsContent value="collectibles">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-display font-bold">Manage Collectibles</h3>
                  <Button size="sm" onClick={() => setCollectibleModal({ open: true, mode: 'create', data: null })} className="bg-primary text-primary-foreground" data-testid="new-collectible-button">
                    <Plus className="w-4 h-4 mr-1" /> New Collectible
                  </Button>
                </div>
                {drops.length === 0 && <p className="text-muted-foreground text-sm mb-4">No drops available. New collectibles can be created as standalone items.</p>}
                <div className="space-y-3">
                  {collectibles.map(col => (
                    <div key={col.collectible_id} className="bg-card border border-border/40 rounded-sm p-4 flex items-center gap-4">
                      <img src={col.image_url} alt="" className="w-16 h-16 rounded object-cover bg-muted flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display font-bold text-sm">{col.name}</h4>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={col.metal_type === 'gold' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-400/20 text-slate-300'}>
                            {col.metal_type?.toUpperCase()} {col.weight}g
                          </Badge>
                          <Badge variant="outline" className="text-xs">{col.purity}</Badge>
                          <span className="text-xs text-muted-foreground">Stock: {col.quantity_remaining}/{col.quantity_total}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => setCollectibleModal({ open: true, mode: 'edit', data: col })}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteConfirm({ open: true, type: 'collectible', id: col.collectible_id, name: col.name })} data-testid={`delete-collectible-${col.collectible_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {collectibles.length === 0 && <p className="text-muted-foreground text-center py-8">No collectibles yet.</p>}
                </div>
              </TabsContent>

              {/* Orders Tab */}
              <TabsContent value="orders">
                <div className="border border-border rounded p-4 mb-4 text-sm" aria-label="Order notification setup">
                  <h3 className="font-semibold">Order alerts and customer confirmations</h3>
                  {notificationHealth?.channels && Object.entries(notificationHealth.channels).map(([channel, health]) => (
                    <p key={channel} className="mt-2">{channel === 'email' ? 'Email' : 'WhatsApp'}: {health.ready ? 'Configuration present — provider verification and delivery test still required' : `Setup required: ${health.missing.join(', ')}`}</p>
                  ))}
                  <div className="flex gap-2 flex-wrap mt-3">
                    <Button size="sm" variant="outline" onClick={refreshNotifications}>Refresh delivery status</Button>
                    {['email', 'whatsapp'].map(channel => <Button key={channel} size="sm" variant="outline" disabled={!notificationHealth?.channels?.[channel]?.ready || notificationTestBusy} onClick={() => runNotificationTest(channel)}>Test {channel === 'email' ? 'email' : 'WhatsApp'} to owner</Button>)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Setup tests use synthetic details and go only to the configured owner contact. They do not create an order or payment.</p>
                  {notificationHealth?.tests?.map((test, index) => <p key={index} className="text-xs mt-1">Owner {test.channel} setup test: {test.status}{test.negative_signal ? ' — provider reported a problem' : ''}</p>)}
                  {!notificationHealth && <p>Checking sender setup…</p>}
                  {notificationHealth?.unavailable && <p>Sender status unavailable. Delivery is not verified.</p>}
                  <p className="text-xs text-muted-foreground mt-2">Delivery service: {notificationHealth?.service?.status || 'checking'}. Accepted means the provider accepted the message. Delivered and read are separate statuses. Old orders are not automatically resent.</p>
                </div>
                <div className="space-y-3">
                  {orders.map(order => (
                    <div key={order.order_id} className="bg-card border border-border/40 rounded-sm p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-mono text-xs text-muted-foreground">{order.order_id}</span>
                            <Badge variant="outline" className="text-xs">{order.metal_type?.toUpperCase()} {order.weight}g</Badge>
                            {order.payment_status === 'paid' && <Badge className="text-[10px] bg-green-500/20 text-green-400 border-green-500/30">Paid</Badge>}
                            {(() => {
                              const cert = certificates.find(c => c.order_id === order.order_id);
                              if (!cert) return null;
                              return (
                                <Badge variant={cert.engraving_status === 'engraved' ? 'default' : 'outline'} className={`text-[10px] ${cert.engraving_status === 'engraved' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'}`}>
                                  {cert.engraving_status === 'engraved' ? 'Engraved' : 'Engrave Pending'}
                                </Badge>
                              );
                            })()}
                          </div>
                          <h4 className="font-display font-bold text-sm truncate">{order.collectible_name}</h4>
                          <p className="text-xs text-muted-foreground">{order.buyer_name || users.find(u => u.user_id === order.user_id)?.name || 'Unknown buyer'} | Rs {order.purchase_price?.toLocaleString()}</p>
                          <p className="text-xs">Payment: {order.payment_status || 'Unknown'} · Fulfilment: {order.status}</p>
                          <details className="mt-2 text-xs"><summary className="cursor-pointer">Contact, delivery and notifications</summary>
                            <p>{order.buyer_email || 'Email not available'}</p>
                            <p>{order.buyer_phone || 'Phone not collected'}</p>
                            <p className="whitespace-pre-line">{order.shipping_address || 'Delivery address not collected — contact buyer before fulfilment'}</p>
                            <p>{order.notification_summary || 'No delivery record'}</p>
                            {order.notification_records?.length ? order.notification_records.map((event, index) => <p key={index}>{event.event}: {event.audience} {event.channel} — {event.status}{event.last_error ? ` (${event.last_error})` : ''}{event.negative_signal ? ' — provider reported a failure or complaint; review required' : ''}</p>) : Object.entries(order.notification_events || {}).map(([key, event]) => <p key={key}>{event.event}: {event.audience} {event.channel} — {event.status === 'not_consented' ? 'Not consented' : 'No dispatch record yet'}</p>)}
                          </details>
                          {(() => {
                            const cert = certificates.find(c => c.order_id === order.order_id);
                            return cert ? <p className="text-[11px] text-gold font-mono mt-0.5">Serial: {cert.serial_number}</p> : null;
                          })()}
                          {/* Shipping Info Summary */}
                          {(order.tracking_number || order.carrier) && (
                            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                              {order.carrier && <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Truck className="w-3 h-3" /> {order.carrier}</span>}
                              {order.tracking_number && <span className="text-[11px] font-mono text-muted-foreground"># {order.tracking_number}</span>}
                              {order.estimated_delivery && <span className="text-[11px] text-muted-foreground">Est: {new Date(order.estimated_delivery).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</span>}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => openShippingModal(order)} data-testid={`shipping-btn-${order.order_id}`}>
                            <Truck className="w-3.5 h-3.5 mr-1" /> Shipping
                          </Button>
                          <Select value={order.status} onValueChange={(val) => handleStatusUpdate(order.order_id, val)}>
                            <SelectTrigger className="w-44 bg-muted/50 border-border h-9 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {STATUS_OPTIONS.map(s => (
                                <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive hover:text-destructive" onClick={() => setDeleteConfirm({ open: true, type: 'order', id: order.order_id, name: order.collectible_name || order.order_id })} data-testid={`delete-order-${order.order_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {orders.length === 0 && <p className="text-muted-foreground text-center py-8">No orders yet</p>}
                </div>
              </TabsContent>

              {/* News Tab */}
              <TabsContent value="news">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-display font-bold">Manage News</h3>
                  <Button size="sm" onClick={() => setNewsModal({ open: true })} className="bg-primary text-primary-foreground">
                    <Plus className="w-4 h-4 mr-1" /> Add News
                  </Button>
                </div>
                <div className="space-y-3">
                  {news.map(item => (
                    <div key={item.news_id} className="bg-card border border-border/40 rounded-sm p-4 flex items-center gap-4">
                      {item.image_url && <img src={item.image_url} alt="" className="w-16 h-16 rounded object-cover bg-muted flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display font-bold text-sm">{item.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-1">{item.summary}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{item.source}</Badge>
                          <Badge className={item.impact === 'positive' ? 'bg-green-500/20 text-green-400' : item.impact === 'negative' ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/60'}>{item.impact}</Badge>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive flex-shrink-0" onClick={() => setDeleteConfirm({ open: true, type: 'news', id: item.news_id, name: item.title })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {news.length === 0 && <p className="text-muted-foreground text-center py-8">No news articles yet.</p>}
                </div>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users">
                <div className="space-y-3">
                  {users.map(u => (
                    <div key={u.user_id} className="bg-card border border-border/40 rounded-sm p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        {u.picture ? <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <span className="text-primary font-bold">{u.name?.[0]?.toUpperCase()}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{u.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {u.is_admin && <Badge className="bg-primary/20 text-primary text-xs">Admin</Badge>}
                        <span className="text-xs text-muted-foreground font-mono">{u.user_id}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Certificates Tab */}
              <TabsContent value="certificates">
                <div className="space-y-3">
                  {certificates.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No certificates yet. Certificates are created when orders are completed.</p>
                  ) : certificates.map(cert => (
                    <div key={cert.certificate_id} className="bg-card border border-border/40 rounded-sm p-4" data-testid={`admin-cert-${cert.certificate_id}`}>
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-xs text-muted-foreground">{cert.certificate_id}</span>
                            <Badge variant="outline" className="text-xs">{cert.metal_type?.toUpperCase()} {cert.weight}g</Badge>
                            <Badge variant={cert.engraving_status === 'engraved' ? 'default' : 'outline'} className={`text-[10px] ${cert.engraving_status === 'engraved' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'}`}>
                              {cert.engraving_status === 'engraved' ? 'Engraved' : 'Needs Engraving'}
                            </Badge>
                          </div>
                          <h4 className="font-display font-bold text-sm">{cert.collectible_name}</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Serial: <span className="font-mono text-gold">{cert.serial_number}</span> | Purity: {cert.purity} | Hash: <span className="font-mono">{cert.certificate_hash?.slice(0, 12)}...</span>
                          </p>
                          {cert.engraving_status === 'pending' && (
                            <p className="text-[11px] text-yellow-400/80 mt-1">
                              Engrave serial <span className="font-mono font-bold">{cert.serial_number}</span> on the physical product before shipping
                            </p>
                          )}
                          {cert.engraved_at && (
                            <p className="text-[11px] text-green-400/60 mt-1">Engraved at: {new Date(cert.engraved_at).toLocaleString()}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {cert.qr_code_url && cert.qr_code_url.startsWith('/api/') && (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${cert.qr_code_url}`} alt="QR" className="w-12 h-12 rounded bg-white p-0.5" />
                          )}
                          <Button
                            size="sm"
                            variant={cert.engraving_status === 'engraved' ? 'outline' : 'default'}
                            onClick={async () => {
                              try {
                                const res = await updateEngraving(cert.certificate_id);
                                setCertificates(prev => prev.map(c => c.certificate_id === cert.certificate_id ? { ...c, engraving_status: res.data.engraving_status, engraved_at: res.data.engraving_status === 'engraved' ? new Date().toISOString() : null } : c));
                                toast.success(res.data.message);
                              } catch { toast.error('Failed to update'); }
                            }}
                            className="text-xs rounded-full"
                            data-testid={`engrave-btn-${cert.certificate_id}`}
                          >
                            {cert.engraving_status === 'engraved' ? 'Undo Engrave' : 'Mark Engraved'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* User Management Tab */}
              <TabsContent value="usersmgmt">
                <UserManagementTab />
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings">
                <SettingsTab />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Drop Modal */}
      <DropModal 
        open={dropModal.open} 
        mode={dropModal.mode} 
        data={dropModal.data}
        onClose={() => setDropModal({ open: false, mode: 'create', data: null })}
        onSave={async (formData) => {
          try {
            if (dropModal.mode === 'create') {
              const res = await adminCreateDrop(formData);
              setDrops(prev => [{ ...formData, drop_id: res.data.drop_id, collectible_ids: [], status: 'upcoming' }, ...prev]);
              toast.success('Drop created');
            } else {
              await adminUpdateDrop(dropModal.data.drop_id, formData);
              setDrops(prev => prev.map(d => d.drop_id === dropModal.data.drop_id ? { ...d, ...formData } : d));
              toast.success('Drop updated');
            }
            setDropModal({ open: false, mode: 'create', data: null });
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save drop');
          }
        }}
      />

      {/* Collectible Modal */}
      <CollectibleModal 
        open={collectibleModal.open}
        mode={collectibleModal.mode}
        data={collectibleModal.data}
        drops={drops}
        onClose={() => setCollectibleModal({ open: false, mode: 'create', data: null })}
        onSave={async (formData) => {
          try {
            if (collectibleModal.mode === 'create') {
              const res = await adminCreateCollectible(formData);
              setCollectibles(prev => [{ ...formData, collectible_id: res.data.collectible_id, quantity_remaining: formData.quantity_total }, ...prev]);
              toast.success('Collectible created');
            } else {
              await adminUpdateCollectible(collectibleModal.data.collectible_id, formData);
              setCollectibles(prev => prev.map(c => c.collectible_id === collectibleModal.data.collectible_id ? { ...c, ...formData } : c));
              toast.success('Collectible updated');
            }
            setCollectibleModal({ open: false, mode: 'create', data: null });
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save collectible');
          }
        }}
      />

      {/* News Modal */}
      <NewsModal
        open={newsModal.open}
        onClose={() => setNewsModal({ open: false })}
        onSave={async (formData) => {
          try {
            const res = await adminCreateNews(formData);
            setNews(prev => [{ ...formData, news_id: res.data.news_id, published_at: new Date().toISOString() }, ...prev]);
            toast.success('News article added');
            setNewsModal({ open: false });
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add news');
          }
        }}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: '', id: '', name: '' })}>
        <DialogContent className="bg-background border-border">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">Are you sure you want to delete <strong className="text-foreground">{deleteConfirm.name}</strong>? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm({ open: false, type: '', id: '', name: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipping Details Modal */}
      <Dialog open={shippingModal.open} onOpenChange={(open) => !open && setShippingModal({ open: false, orderId: '', data: {} })}>
        <DialogContent className="bg-background border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Shipping Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-muted-foreground font-mono">{shippingModal.orderId}</p>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Carrier / Logistics Partner</label>
              <Input
                placeholder="Enter carrier name"
                value={shippingModal.data.carrier || ''}
                onChange={e => setShippingModal(p => ({ ...p, data: { ...p.data, carrier: e.target.value } }))}
                data-testid="shipping-carrier-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Tracking Number</label>
              <Input
                placeholder="e.g. SR1234567890"
                value={shippingModal.data.tracking_number || ''}
                onChange={e => setShippingModal(p => ({ ...p, data: { ...p.data, tracking_number: e.target.value } }))}
                data-testid="shipping-tracking-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Estimated Delivery Date</label>
              <Input
                type="date"
                value={shippingModal.data.estimated_delivery || ''}
                onChange={e => setShippingModal(p => ({ ...p, data: { ...p.data, estimated_delivery: e.target.value } }))}
                data-testid="shipping-delivery-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Shipping Address</label>
              <Input
                placeholder="Full shipping address"
                value={shippingModal.data.shipping_address || ''}
                onChange={e => setShippingModal(p => ({ ...p, data: { ...p.data, shipping_address: e.target.value } }))}
                data-testid="shipping-address-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShippingModal({ open: false, orderId: '', data: {} })}>Cancel</Button>
            <Button onClick={handleShippingSave} className="bg-primary text-primary-foreground" data-testid="shipping-save-btn">
              <Save className="w-4 h-4 mr-1" /> Save Shipping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Drop Modal ──────────────────────────────────────────────── */
function DropModal({ open, mode, data, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', description: '', release_date: '', image_url: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data && mode === 'edit') {
      setForm({ name: data.name, description: data.description, release_date: data.release_date, image_url: data.image_url });
    } else {
      setForm({ name: '', description: '', release_date: '', image_url: '' });
    }
  }, [data, mode, open]);

  const handleSubmit = async () => {
    if (!form.name || !form.release_date) {
      toast.error('Name and release date are required');
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-background border-border max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create New Drop' : 'Edit Drop'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Drop Name *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Genesis Collection" className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="The first-ever collection..." className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Release Date *</label>
            <Input type="date" value={form.release_date} onChange={(e) => setForm({ ...form, release_date: e.target.value })} className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Drop Image</label>
            <ImageUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-primary text-primary-foreground">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Drop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Collectible Modal ───────────────────────────────────────── */
function CollectibleModal({ open, mode, data, drops, onClose, onSave }) {
  const [form, setForm] = useState({
    name: '', metal_type: 'gold', weight: 1, purity: '22K (916)', purity_fineness: 916, purity_karat: '22K',
    bis_hallmark: 'BIS 916', design_type: 'coin', category: '', serial_number_prefix: 'ZWT',
    pricing_mode: 'fixed_supplier_cost', supplier_cost: '', supplier_wastage_percent: '',
    supplier_making_cost: 0, supplier_other_cost: 0, margin_percent: 13,
    making_cost: 0, packaging_cost: '', packaging_cost_confirmed: false,
    shipping_cost: '', shipping_cost_confirmed: false,
    allocated_overhead_cost: '', allocated_overhead_cost_confirmed: false,
    customer_delivery_cost: '', customer_delivery_cost_confirmed: false, platform_margin: 0,
    mint_partner: '', drop_id: '', quantity_total: 100, image_url: '', images: [], description: ''
  });
  const [saving, setSaving] = useState(false);
  const [customCategory, setCustomCategory] = useState('');

  useEffect(() => {
    if (data && mode === 'edit') {
      setForm({
        name: data.name, metal_type: data.metal_type, weight: data.weight, purity: data.purity,
        purity_fineness: data.purity_fineness, purity_karat: data.purity_karat, bis_hallmark: data.bis_hallmark,
        design_type: data.design_type, category: data.category || data.design_type, serial_number_prefix: data.serial_number,
        pricing_mode: data.pricing_mode || (data.supplier_cost ? 'fixed_supplier_cost' : 'live_components'),
        supplier_cost: data.supplier_cost || '', supplier_wastage_percent: data.supplier_wastage_percent ?? '',
        supplier_making_cost: data.supplier_making_cost ?? data.making_cost ?? 0,
        supplier_other_cost: data.supplier_other_cost || 0, margin_percent: data.margin_percent ?? 13,
        making_cost: 0, packaging_cost: data.packaging_cost ?? '', packaging_cost_confirmed: data.packaging_cost_confirmed === true,
        shipping_cost: data.shipping_cost ?? '', shipping_cost_confirmed: data.shipping_cost_confirmed === true,
        allocated_overhead_cost: data.allocated_overhead_cost ?? '', allocated_overhead_cost_confirmed: data.allocated_overhead_cost_confirmed === true,
        customer_delivery_cost: data.customer_delivery_cost ?? '', customer_delivery_cost_confirmed: data.customer_delivery_cost_confirmed === true,
        platform_margin: 0,
        mint_partner: data.mint_partner || '', drop_id: data.drop_id || '', quantity_total: data.quantity_total, image_url: data.image_url, 
        images: data.images || [], description: data.description
      });
    } else {
      setForm(prev => ({
        ...prev,
        drop_id: '', images: [], category: '', pricing_mode: 'fixed_supplier_cost', supplier_cost: '',
        supplier_wastage_percent: '', supplier_making_cost: 0, supplier_other_cost: 0, margin_percent: 13,
        packaging_cost: '', packaging_cost_confirmed: false, shipping_cost: '', shipping_cost_confirmed: false,
        allocated_overhead_cost: '', allocated_overhead_cost_confirmed: false,
        customer_delivery_cost: '', customer_delivery_cost_confirmed: false,
      }));
    }
  }, [data, mode, open, drops]);

  const purities = form.metal_type === 'gold' ? GOLD_PURITIES : SILVER_PURITIES;

  const handlePurityChange = (label) => {
    const p = purities.find(x => x.label === label);
    if (p) {
      setForm({ ...form, purity: label, purity_fineness: p.fineness, purity_karat: p.karat, bis_hallmark: p.bis });
    }
  };

  const addAdditionalImage = (url) => {
    if (url && !form.images.includes(url)) {
      setForm({ ...form, images: [...form.images, url] });
    }
  };

  const removeAdditionalImage = (index) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== index) });
  };

  const handleSubmit = async () => {
    const fixedCostMissing = form.pricing_mode === 'fixed_supplier_cost' && Number(form.supplier_cost) <= 0;
    const liveWastageMissing = form.pricing_mode === 'live_components' && form.supplier_wastage_percent === '';
    const confirmedCostMissing = [
      ['packaging_cost', 'packaging_cost_confirmed'], ['shipping_cost', 'shipping_cost_confirmed'],
      ['allocated_overhead_cost', 'allocated_overhead_cost_confirmed'], ['customer_delivery_cost', 'customer_delivery_cost_confirmed'],
    ].some(([amount, confirmed]) => form[confirmed] && form[amount] === '');
    if (!form.name || !form.image_url || fixedCostMissing || liveWastageMissing || confirmedCostMissing) {
      toast.error(liveWastageMissing ? 'Enter this product’s supplier wastage percentage (0% is allowed)' : confirmedCostMissing ? 'Enter an amount for every confirmed business cost (₹0 is allowed)' : 'Name, image, and fixed supplier cost are required');
      return;
    }
    // Use custom category if provided, otherwise use design_type
    const finalForm = {
      ...form,
      category: customCategory.trim() || form.category || form.design_type,
      drop_id: form.drop_id || null,  // Send null if no drop selected
      supplier_cost: form.pricing_mode === 'fixed_supplier_cost' ? Number(form.supplier_cost) : null,
      supplier_wastage_percent: form.supplier_wastage_percent === '' ? null : Number(form.supplier_wastage_percent),
      supplier_making_cost: Number(form.supplier_making_cost || 0),
      supplier_other_cost: Number(form.supplier_other_cost || 0),
      margin_percent: 13,
      packaging_cost: form.packaging_cost === '' ? null : Number(form.packaging_cost),
      shipping_cost: form.shipping_cost === '' ? null : Number(form.shipping_cost),
      allocated_overhead_cost: form.allocated_overhead_cost === '' ? null : Number(form.allocated_overhead_cost),
      customer_delivery_cost: form.customer_delivery_cost === '' ? null : Number(form.customer_delivery_cost),
      making_cost: Number(form.supplier_making_cost || 0),
    };
    setSaving(true);
    await onSave(finalForm);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-background border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create New Collectible' : 'Edit Collectible'}</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Name *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Shiva Coin" className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Drop <span className="text-muted-foreground/50">(Optional)</span></label>
            <Select value={form.drop_id || "_none"} onValueChange={(v) => setForm({ ...form, drop_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="bg-secondary border-border" data-testid="collectible-drop-select"><SelectValue placeholder="No drop (standalone)" /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="_none" className="text-muted-foreground">No drop (standalone collectible)</SelectItem>
                {drops.map(d => <SelectItem key={d.drop_id} value={d.drop_id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-0.5">Leave empty to create standalone collectible not tied to any drop</p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Metal Type</label>
            <Select value={form.metal_type} onValueChange={(v) => { setForm({ ...form, metal_type: v }); }}>
              <SelectTrigger className="bg-secondary border-border" data-testid="metal-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {METAL_TYPES.map(m => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Weight (grams)</label>
            <Input type="number" min="0.5" step="0.5" value={form.weight} onChange={(e) => setForm({ ...form, weight: parseFloat(e.target.value) })} className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Purity</label>
            <Select value={form.purity} onValueChange={handlePurityChange}>
              <SelectTrigger className="bg-secondary border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {purities.map(p => <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Design Type</label>
            <Select value={form.design_type} onValueChange={(v) => setForm({ ...form, design_type: v, category: v })}>
              <SelectTrigger className="bg-secondary border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border max-h-60">
                {['coin', 'bar', 'pendant', 'medallion', 'ring', 'chain', 'bracelet', 'earring', 'necklace', 'bangle', 'anklet', 'nose-pin', 'mangalsutra', 'kada', 'toe-ring', 'waistband'].map(d => <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1).replace('-', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Custom Category <span className="text-muted-foreground/50">(Optional)</span></label>
            <Input value={customCategory} onChange={(e) => setCustomCategory(e.target.value)} placeholder="e.g., waistchain, armlet, maang-tikka" className="bg-secondary border-border" />
            <p className="text-[10px] text-muted-foreground mt-0.5">Leave empty to use Design Type as category</p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Serial Prefix</label>
            <Input value={form.serial_number_prefix} onChange={(e) => setForm({ ...form, serial_number_prefix: e.target.value })} placeholder="ZWT" className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Quantity</label>
            <Input type="number" min="1" value={form.quantity_total} onChange={(e) => setForm({ ...form, quantity_total: parseInt(e.target.value) })} className="bg-secondary border-border" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Pricing Method</label>
            <Select value={form.pricing_mode} onValueChange={(v) => setForm({ ...form, pricing_mode: v })}>
              <SelectTrigger className="bg-secondary border-border" data-testid="pricing-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="fixed_supplier_cost">Fixed Supplier Cost</SelectItem>
                <SelectItem value="live_components">Live Metal + Supplier Components</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.pricing_mode === 'fixed_supplier_cost' ? (
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Final Supplier Cost (₹) *</label>
              <Input
                type="number" min="0.01" step="0.01" value={form.supplier_cost}
                onChange={(e) => setForm({ ...form, supplier_cost: e.target.value })}
                className="bg-secondary border-border" data-testid="supplier-cost-input"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">Use the supplier's complete landed cost when no component breakdown is available.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-1 block">Supplier Wastage (%) *</label>
                <Input type="number" min="0" max="100" step="0.1" value={form.supplier_wastage_percent} onChange={(e) => setForm({ ...form, supplier_wastage_percent: e.target.value })} className="bg-secondary border-border" data-testid="supplier-wastage-input" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-1 block">Supplier Making Cost (₹)</label>
                <Input type="number" min="0" step="0.01" value={form.supplier_making_cost} onChange={(e) => setForm({ ...form, supplier_making_cost: e.target.value })} className="bg-secondary border-border" data-testid="supplier-making-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground uppercase mb-1 block">Other Supplier Cost (₹)</label>
                <Input type="number" min="0" step="0.01" value={form.supplier_other_cost} onChange={(e) => setForm({ ...form, supplier_other_cost: e.target.value })} className="bg-secondary border-border" data-testid="supplier-other-cost-input" />
              </div>
            </>
          )}
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Supplier / Mint Partner (Optional)</label>
            <Input value={form.mint_partner} onChange={(e) => setForm({ ...form, mint_partner: e.target.value })} placeholder="e.g., MMTC-PAMP, Kundan" className="bg-secondary border-border" data-testid="supplier-name-input" />
          </div>
          <div className="md:col-span-2 border-t border-border/40 pt-4">
            <p className="text-xs text-muted-foreground uppercase mb-3">Confirmed Business Costs</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ['packaging_cost', 'packaging_cost_confirmed', 'Packaging'],
                ['shipping_cost', 'shipping_cost_confirmed', 'Total freight and insurance cost'],
                ['allocated_overhead_cost', 'allocated_overhead_cost_confirmed', 'Allocated overhead'],
                ['customer_delivery_cost', 'customer_delivery_cost_confirmed', 'Separate delivery charge (₹0 pending tax policy)'],
              ].map(([amount, confirmed, label]) => (
                <div key={amount} className="border border-border/40 p-3 rounded-sm">
                  <label className="text-xs text-muted-foreground mb-1 block">{label} (₹)</label>
                  <Input type="number" min="0" step="0.01" value={form[amount]} onChange={(e) => setForm({ ...form, [amount]: e.target.value })} className="bg-secondary border-border" data-testid={`${amount.replaceAll('_', '-')}-input`} />
                  <label className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                    <input type="checkbox" checked={form[confirmed]} onChange={(e) => setForm({ ...form, [confirmed]: e.target.checked })} data-testid={`${confirmed.replaceAll('_', '-')}-checkbox`} />
                    Confirmed, including intentional ₹0
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 bg-muted/10 border border-border/40 rounded-sm p-3">
            <p className="text-xs text-muted-foreground uppercase mb-2">Cost-based Quote (Not Editable)</p>
            <p className="text-sm text-foreground">Confirmed total business costs + <span className="text-primary font-semibold">13% profit</span> + existing GST.</p>
            <p className="text-[10px] text-muted-foreground mt-1">Quotes stay blocked until every business cost and the platform payment-fee policy are explicitly confirmed.</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Main Product Image *</label>
            <ImageUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Additional Images (Gallery)</label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {form.images.map((img, idx) => (
                <div key={idx} className="relative group">
                  <img src={img} alt={`Additional ${idx + 1}`} className="w-full h-20 object-cover rounded bg-muted" />
                  <button
                    type="button"
                    onClick={() => removeAdditionalImage(idx)}
                    className="absolute top-1 right-1 p-1 bg-red-500/80 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <ImageUpload value="" onChange={addAdditionalImage} />
            <p className="text-xs text-muted-foreground mt-1">Add up to 5 additional images for the product gallery</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Beautiful handcrafted..." className="bg-secondary border-border" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-collectible-button">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Collectible'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── News Modal ──────────────────────────────────────────────── */
function NewsModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({ title: '', source: '', summary: '', url: '', image_url: '', impact: 'neutral' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ title: '', source: '', summary: '', url: '', image_url: '', impact: 'neutral' });
  }, [open]);

  const handleSubmit = async () => {
    if (!form.title || !form.source || !form.summary) {
      toast.error('Title, source, and summary are required');
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-background border-border max-w-lg">
        <DialogHeader>
          <DialogTitle>Add News Article</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Title *</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Gold prices surge..." className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Source *</label>
            <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Economic Times" className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Summary *</label>
            <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Brief summary..." className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Article URL</label>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." className="bg-secondary border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">News Image (optional)</label>
            <ImageUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase mb-1 block">Market Impact</label>
            <Select value={form.impact} onValueChange={(v) => setForm({ ...form, impact: v })}>
              <SelectTrigger className="bg-secondary border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="positive">Positive (Bullish)</SelectItem>
                <SelectItem value="negative">Negative (Bearish)</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-primary text-primary-foreground">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Add News'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── User Management Tab ───────────────────────────────────────── */
function UserManagementTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', is_admin: false, role: 'user' });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await getAdminUsersList();
      setUsers(data);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      if (editUser) {
        await adminUpdateUser(editUser.user_id, { name: form.name, email: form.email, is_admin: form.is_admin, role: form.role });
        toast.success('User updated');
      } else {
        if (!form.password) { toast.error('Password required'); return; }
        await adminCreateUser(form);
        toast.success('User created');
      }
      setShowModal(false);
      setEditUser(null);
      setForm({ name: '', email: '', password: '', is_admin: false, role: 'user' });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await adminDeleteUser(userId);
      toast.success('User deactivated');
      loadUsers();
    } catch { toast.error('Failed'); }
  };

  const handleResetPassword = async (userId) => {
    try {
      const { data } = await adminResetUserPassword(userId);
      toast.success(`Temporary password: ${data.temporary_password}`);
    } catch { toast.error('Failed to reset password'); }
  };

  return (
    <div data-testid="user-management-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <UserCog className="w-5 h-5 text-primary" /> User Management
        </h3>
        <Button size="sm" onClick={() => { setEditUser(null); setForm({ name: '', email: '', password: '', is_admin: false, role: 'user' }); setShowModal(true); }} className="bg-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-1" /> Add User
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.user_id} className="bg-card border border-border/40 rounded-lg p-4 flex items-center justify-between" data-testid={`user-row-${u.user_id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.is_admin ? 'bg-primary/20' : 'bg-secondary'}`}>
                  {u.is_admin ? <UserCog className="w-5 h-5 text-primary" /> : <Users className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div>
                  <p className="font-semibold text-sm">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-[10px] ${u.is_admin ? 'border-primary/40 text-primary' : ''}`}>
                  {u.role || (u.is_admin ? 'admin' : 'user')}
                </Badge>
                {u.is_active === false && <Badge className="bg-red-500/20 text-red-400 text-[10px]">Inactive</Badge>}
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditUser(u); setForm({ name: u.name, email: u.email, password: '', is_admin: u.is_admin, role: u.role || 'user' }); setShowModal(true); }}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleResetPassword(u.user_id)} title="Reset Password">
                  <Key className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400" onClick={() => handleDelete(u.user_id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editUser ? 'Edit User' : 'Add User'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Name</label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-secondary border-border" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Email</label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-secondary border-border" />
            </div>
            {!editUser && (
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-1 block">Password</label>
                <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-secondary border-border" />
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Role</label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v, is_admin: v === 'admin' })}>
                <SelectTrigger className="bg-secondary border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-primary text-primary-foreground">{editUser ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Settings Tab ───────────────────────────────────────────────── */
function SettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ibjaSaving, setIbjaSaving] = useState(false);
  const [ibja, setIbja] = useState({
    gold_999_per_10g: '', silver_999_per_kg: '', session: 'PM', published_at: '', valid_until: '', configured: false,
  });
  const [form, setForm] = useState({
    gst_percent: 3,
    order_cancellation_policy: '',
    min_order_value: 0,
    max_order_value: 10000000,
    payment_fee_percent: '', payment_fee_percent_confirmed: false,
    payment_fee_fixed: '', payment_fee_fixed_confirmed: false,
    payment_fee_gst_percent: '', payment_fee_gst_percent_confirmed: false,
    payment_fee_gst_recoverable: false, payment_fee_gst_recoverable_confirmed: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [{ data }, { data: ibjaData }] = await Promise.all([getAdminSettings(), getAdminIbjaRate()]);
      setForm({
        gst_percent: data.gst_percent ?? 3,
        order_cancellation_policy: data.order_cancellation_policy || '',
        min_order_value: data.min_order_value || 0,
        max_order_value: data.max_order_value || 10000000,
        payment_fee_percent: data.payment_fee_percent ?? '', payment_fee_percent_confirmed: data.payment_fee_percent_confirmed === true,
        payment_fee_fixed: data.payment_fee_fixed ?? '', payment_fee_fixed_confirmed: data.payment_fee_fixed_confirmed === true,
        payment_fee_gst_percent: data.payment_fee_gst_percent ?? '', payment_fee_gst_percent_confirmed: data.payment_fee_gst_percent_confirmed === true,
        payment_fee_gst_recoverable: data.payment_fee_gst_recoverable === true,
        payment_fee_gst_recoverable_confirmed: data.payment_fee_gst_recoverable_confirmed === true,
      });
      if (ibjaData.configured) {
        const toLocalInput = (value) => {
          const date = new Date(value);
          return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        };
        setIbja({
          gold_999_per_10g: ibjaData.gold_999_per_10g,
          silver_999_per_kg: ibjaData.silver_999_per_kg,
          session: ibjaData.session,
          published_at: toLocalInput(ibjaData.published_at),
          valid_until: toLocalInput(ibjaData.valid_until),
          configured: true,
        });
      }
    } catch { toast.error('Failed to load settings'); }
    setLoading(false);
  };

  const handleIbjaSave = async () => {
    if (!ibja.gold_999_per_10g || !ibja.silver_999_per_kg || !ibja.published_at || !ibja.valid_until) {
      toast.error('Enter both official rates, publication time, and validity');
      return;
    }
    setIbjaSaving(true);
    try {
      await updateAdminIbjaRate({
        gold_999_per_10g: Number(ibja.gold_999_per_10g),
        silver_999_per_kg: Number(ibja.silver_999_per_kg),
        session: ibja.session,
        published_at: new Date(ibja.published_at).toISOString(),
        valid_until: new Date(ibja.valid_until).toISOString(),
      });
      toast.success('Official IBJA benchmark saved');
      await loadSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save IBJA benchmark');
    }
    setIbjaSaving(false);
  };

  const handleSave = async () => {
    const confirmedFeeMissing = [
      ['payment_fee_percent', 'payment_fee_percent_confirmed'],
      ['payment_fee_fixed', 'payment_fee_fixed_confirmed'],
      ['payment_fee_gst_percent', 'payment_fee_gst_percent_confirmed'],
    ].some(([amount, confirmed]) => form[confirmed] && form[amount] === '');
    if (confirmedFeeMissing) {
      toast.error('Enter a value for every confirmed payment fee (0 is allowed)');
      return;
    }
    setSaving(true);
    try {
      await updateAdminSettings({
        ...form,
        payment_fee_percent: form.payment_fee_percent === '' ? null : Number(form.payment_fee_percent),
        payment_fee_fixed: form.payment_fee_fixed === '' ? null : Number(form.payment_fee_fixed),
        payment_fee_gst_percent: form.payment_fee_gst_percent === '' ? null : Number(form.payment_fee_gst_percent),
      });
      toast.success('Settings saved');
      loadSettings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-10"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div data-testid="settings-tab">
      <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-6">
        <Settings className="w-5 h-5 text-primary" /> Platform Settings
      </h3>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border/40 rounded-xl p-5 md:col-span-2" data-testid="ibja-benchmark-settings">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h4 className="font-semibold text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-gold" /> Official IBJA Benchmark</h4>
            <Badge variant="outline" data-testid="ibja-benchmark-status">{ibja.configured ? 'Configured' : 'Not configured'}</Badge>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Gold 999 (Rs / 10g)</label>
              <Input type="number" min="0" step="0.01" value={ibja.gold_999_per_10g} onChange={e => setIbja({ ...ibja, gold_999_per_10g: e.target.value })} className="bg-secondary border-border" data-testid="ibja-gold-rate-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Silver 999 (Rs / kg)</label>
              <Input type="number" min="0" step="0.01" value={ibja.silver_999_per_kg} onChange={e => setIbja({ ...ibja, silver_999_per_kg: e.target.value })} className="bg-secondary border-border" data-testid="ibja-silver-rate-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Session</label>
              <Select value={ibja.session} onValueChange={session => setIbja({ ...ibja, session })}>
                <SelectTrigger className="bg-secondary border-border" data-testid="ibja-session-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Official publication</label>
              <Input type="datetime-local" value={ibja.published_at} onChange={e => setIbja({ ...ibja, published_at: e.target.value })} className="bg-secondary border-border" data-testid="ibja-published-at-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Valid until</label>
              <Input type="datetime-local" value={ibja.valid_until} onChange={e => setIbja({ ...ibja, valid_until: e.target.value })} className="bg-secondary border-border" data-testid="ibja-valid-until-input" />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <p className="text-xs text-muted-foreground">Enter the latest official IBJA Gold 999 per 10g and Silver 999 per kg, before GST. Pricing fails closed after the validity time.</p>
            <Button onClick={handleIbjaSave} disabled={ibjaSaving} data-testid="save-ibja-benchmark-button">
              <Save className="w-4 h-4 mr-2" /> {ibjaSaving ? 'Saving...' : 'Save IBJA Benchmark'}
            </Button>
          </div>
        </div>

        {/* Pricing Settings */}
        <div className="bg-card border border-border/40 rounded-xl p-5">
          <h4 className="font-semibold text-sm mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-gold" /> Product Pricing</h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Supplier cost</span><span>Set per collectible</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">SŌHM profit</span><span className="text-gold font-semibold">13% target on total cost</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Global wastage</span><span>None</span></div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">GST (%)</label>
              <Input type="number" min="0" max="28" step="0.1" value={form.gst_percent} onChange={e => setForm({ ...form, gst_percent: parseFloat(e.target.value) || 0 })} className="bg-secondary border-border" data-testid="gst-percent-input" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">Supplier computation remains product-specific. Confirmed business and payment costs are included before the 13% profit target; dasvandh is an internal allocation after profit.</p>
        </div>

        <div className="bg-card border border-border/40 rounded-xl p-5 md:col-span-2" data-testid="payment-fee-settings">
          <h4 className="font-semibold text-sm mb-4">Payment Cost Policy</h4>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              ['payment_fee_percent', 'payment_fee_percent_confirmed', 'Percentage fee (%)'],
              ['payment_fee_fixed', 'payment_fee_fixed_confirmed', 'Fixed fee (₹)'],
              ['payment_fee_gst_percent', 'payment_fee_gst_percent_confirmed', 'Fee GST (%)'],
            ].map(([amount, confirmed, label]) => (
              <div key={amount} className="border border-border/40 p-3 rounded-sm">
                <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
                <Input type="number" min="0" step="0.01" value={form[amount]} onChange={e => setForm({ ...form, [amount]: e.target.value })} className="bg-secondary border-border" data-testid={`${amount.replaceAll('_', '-')}-input`} />
                <label className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                  <input type="checkbox" checked={form[confirmed]} onChange={e => setForm({ ...form, [confirmed]: e.target.checked })} data-testid={`${confirmed.replaceAll('_', '-')}-checkbox`} />
                  Confirmed, including 0
                </label>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-5 mt-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.payment_fee_gst_recoverable} onChange={e => setForm({ ...form, payment_fee_gst_recoverable: e.target.checked })} data-testid="payment-fee-gst-recoverable-checkbox" />
              Fee GST is recoverable
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={form.payment_fee_gst_recoverable_confirmed} onChange={e => setForm({ ...form, payment_fee_gst_recoverable_confirmed: e.target.checked })} data-testid="payment-fee-gst-recoverable-confirmed-checkbox" />
              Recoverability confirmed
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Leave unknown values unconfirmed. ₹0 is used only when entered and confirmed explicitly.</p>
        </div>

        {/* Order Settings */}
        <div className="bg-card border border-border/40 rounded-xl p-5">
          <h4 className="font-semibold text-sm mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Order Policies</h4>
          
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-1 block">Cancellation Policy</label>
              <Textarea value={form.order_cancellation_policy} onChange={e => setForm({ ...form, order_cancellation_policy: e.target.value })} className="bg-secondary border-border min-h-[80px]" placeholder="Orders cannot be cancelled..." />
              <p className="text-[10px] text-muted-foreground mt-0.5">Shown to customers at checkout</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-1 block">Min Order (Rs)</label>
                <Input type="number" min="0" value={form.min_order_value} onChange={e => setForm({ ...form, min_order_value: parseInt(e.target.value) || 0 })} className="bg-secondary border-border" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-1 block">Max Order (Rs)</label>
                <Input type="number" min="0" value={form.max_order_value} onChange={e => setForm({ ...form, max_order_value: parseInt(e.target.value) || 0 })} className="bg-secondary border-border" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
